/**
 * <lunched-rating>
 *
 * Fetches per-product review aggregates from WiseReviews' public proxy
 * (https://wise-reviews.codexapps.co/api/proxy/reviews) on every page
 * load and renders our own yellow-gold SVG star badge. Optional review-
 * count link (mode="stars-with-count") smooth-scrolls to the
 * <wise-product-reviews> section on the same page.
 *
 * Attributes:
 *   data-product-id  (required)  - Shopify product ID.
 *   data-mode        (optional)  - "stars" | "stars-with-count". Default "stars".
 *   data-size        (optional)  - Star size in px. Default 14.
 *
 * Empty state: renders nothing (hidden attribute set) when reviews_count === 0
 * or on any fetch failure. WiseReviews going down does not break the storefront.
 *
 * No client-side caching: earlier versions cached aggregates in
 * sessionStorage with a 15-minute TTL. That cached an initial `count: 0`
 * response through 15 minutes of the visitor's session even after the
 * merchant added a review, causing the pill to stay hidden. The merchant
 * asked for real-time counts, so caching was removed. IntersectionObserver
 * still gates fetches for below-fold product cards so collection pages
 * don't fire N concurrent WR requests up front.
 *
 * Coupling note (see LESSONS.md): the API URL is coupled to WiseReviews. If the
 * merchant migrates to a different review app, only the `_fetchAggregates`
 * method needs updating - the CE public shape stays the same.
 */

const API_ORIGIN = 'https://wise-reviews.codexapps.co';
const API_PATH = '/api/proxy/reviews';
const FETCH_TIMEOUT_MS = 5000;
const IO_ROOT_MARGIN = '200px'; // preempt scroll

class LunchedRating extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._observer = null;
    this._hasFetched = false;
    this._fetchInProgress = false;
    this._countLink = null;
    this._onCountClick = this._onCountClick.bind(this);
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    if (!productId) return;

    if (this._isPdp() || this._isInViewport()) {
      this._enqueueFetch(productId, false);
    } else {
      this._observeAndFetch(productId);
    }
  }

  disconnectedCallback() {
    if (this._abortController) this._abortController.abort();
    if (this._observer) this._observer.disconnect();
    if (this._countLink) {
      this._countLink.removeEventListener('click', this._onCountClick);
      this._countLink = null;
    }
  }

  _isPdp() {
    return document.body.classList.contains('template-product') ||
           document.body.matches('[data-template*="product"]') ||
           /^\/products\//.test(location.pathname);
  }

  _isInViewport() {
    const r = this.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0;
  }

  _observeAndFetch(productId) {
    this._observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting) && !this._hasFetched) {
        this._observer.disconnect();
        this._observer = null;
        this._enqueueFetch(productId, false);
      }
    }, { rootMargin: IO_ROOT_MARGIN });
    this._observer.observe(this);
  }

  async _enqueueFetch(productId, silent) {
    if (this._hasFetched || this._fetchInProgress) return;
    this._fetchInProgress = true;

    try {
      const aggregates = await this._fetchAggregates(productId);
      this._render(aggregates);
      this._hasFetched = true;
    } catch (err) {
      if (!silent) {
        // eslint-disable-next-line no-console
        console.error('[lunched-rating]', 'fetch-failed', { productId, error: String(err) });
        this._hide();
      }
    } finally {
      this._fetchInProgress = false;
    }
  }

  async _fetchAggregates(productId) {
    const controller = new AbortController();
    this._abortController = controller;
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const shop = window.Shopify && window.Shopify.shop
      ? window.Shopify.shop
      : location.hostname;
    const params = new URLSearchParams({
      shop,
      product_id: productId,
      type: 'product_reviews',
      sort_by: 'RECOMMENDED',
      deviceType: 'desktop',
      preview: 'false'
    });

    try {
      const res = await fetch(`${API_ORIGIN}${API_PATH}?${params.toString()}`, {
        signal: controller.signal
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json || typeof json !== 'object') throw new Error('bad-shape');
      // Defensive extraction. WR has returned `meta.total` as a number
      // historically but the shape has drifted before - we coerce
      // whatever is there and fall back through several candidates so
      // the pill never silently hides on a shape wobble:
      //   1. `meta.total`                        (documented shape)
      //   2. `meta.to`                           (some WR responses omit total but populate `to` for the last-page count)
      //   3. `data.length`                       (the reviews array itself)
      //   4. `rating_percentage` sum-inference   (last resort, rarely needed)
      // `average_rating` similarly comes as either a number or a string
      // depending on WR version; parseFloat handles both.
      const avg = parseFloat(json.average_rating) || 0;
      let count = 0;
      const candidates = [
        json.meta && json.meta.total,
        json.meta && json.meta.to,
        Array.isArray(json.data) ? json.data.length : null,
      ];
      for (const c of candidates) {
        const n = parseInt(c, 10);
        if (Number.isFinite(n) && n > 0) { count = n; break; }
      }
      if (count <= 0) {
        return { avg: 0, count: 0 };
      }
      return {
        avg: Math.max(0, Math.min(5, avg)),
        count
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  _hide() {
    this.setAttribute('hidden', '');
    this.innerHTML = '';
  }

  _render(aggregates) {
    const { avg, count } = aggregates;
    if (!count || count <= 0) {
      this._hide();
      return;
    }

    this.removeAttribute('hidden');

    const mode = this.dataset.mode === 'stars-with-count' ? 'stars-with-count' : 'stars';
    const size = parseInt(this.dataset.size, 10) || 14;
    const percent = Math.round((avg / 5) * 100);
    const avgFormatted = avg.toFixed(1);
    const countFormatted = this._formatCount(count);
    const label = `Rated ${avgFormatted} out of 5 based on ${countFormatted} reviews`;

    this.classList.add('lunched-rating', `lunched-rating--mode-${mode}`);
    this.style.setProperty('--star-size', `${size}px`);

    const starsHtml = this._starsHtml(percent, label);
    const countHtml = mode === 'stars-with-count'
      ? `<a class="lunched-rating__count" href="#reviews" aria-label="Read all ${countFormatted} reviews">${countFormatted} reviews</a>`
      : '';

    this.innerHTML = starsHtml + countHtml;

    if (mode === 'stars-with-count') {
      this._countLink = this.querySelector('.lunched-rating__count');
      if (this._countLink) this._countLink.addEventListener('click', this._onCountClick);
    }
  }

  _starsHtml(percent, ariaLabel) {
    // Two identical rows of 5 stars. The outlined row is the base, the filled row
    // is absolutely positioned on top and clipped by width: var(--fill-percent).
    // Sprite URL from <html data-icon-sprite="..."> per the theme's icon convention
    // (see snippets/icon.liquid). Falls back to same-doc #ref in theme design mode.
    const sprite = document.documentElement.dataset.iconSprite || '';
    const outlined = Array.from({ length: 5 }, () =>
      `<svg class="lunched-rating__star" aria-hidden="true" focusable="false"><use href="${sprite}#icon-star"/></svg>`
    ).join('');
    const filled = Array.from({ length: 5 }, () =>
      `<svg class="lunched-rating__star lunched-rating__star--filled" aria-hidden="true" focusable="false"><use href="${sprite}#icon-star-filled"/></svg>`
    ).join('');
    return `
      <span class="lunched-rating__stars" role="img" aria-label="${ariaLabel}" style="--fill-percent: ${percent}%;">
        <span class="lunched-rating__stars-layer lunched-rating__stars-layer--outlined">${outlined}</span>
        <span class="lunched-rating__stars-layer lunched-rating__stars-layer--filled">${filled}</span>
      </span>
    `;
  }

  _formatCount(count) {
    try {
      const locale = (document.documentElement.lang) || 'en-PH';
      return new Intl.NumberFormat(locale).format(count);
    } catch {
      return String(count);
    }
  }

  _onCountClick(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const target = document.querySelector('wise-product-reviews');
    if (!target) return; // let default anchor behavior handle it (or no-op if no #reviews element)
    event.preventDefault();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }
}

if (!customElements.get('lunched-rating')) {
  customElements.define('lunched-rating', LunchedRating);
}
